import { useState } from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  Chip,
  Table,
  Paper,
  Stack,
  Alert,
  Dialog,
  Button,
  Tooltip,
  TableRow,
  Collapse,
  TableBody,
  TableCell,
  TableHead,
  Typography,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableContainer
} from '@mui/material';

import { getPlatformDisplayName } from 'src/utils/platform-label';

import Iconify from 'src/components/iconify';

import { rowStatus, delistRelistSummary } from './utils/delist-relist-summary';

export default function DelistRelistResultsDialog ({ open, onClose, results }) {
  const [expandedRows, setExpandedRows] = useState({});

  if (!results) return null;

  // Only the rows are read here now; every count the summary needs it takes from `results` itself.
  const { details } = results;

  // Platforms to show in the "Platforms" column for a given listing detail.
  // - For non-skipped rows: union of attempted platforms (delistedFrom ∪ failedDelist platforms).
  //   This reflects what the worker actually tried, which equals the user's selection ∩ what
  //   was on the listing — exactly what users expect to see, not the full original list.
  // - For skipped rows: empty (no platforms were touched).
  const platformsForRow = (detail) => {
    if (detail.status === 'skipped') return [];
    const attempted = new Set();
    (detail.delistedFrom || []).forEach(p => attempted.add(p));
    (detail.failedDelist || []).forEach(item => {
      const platform = typeof item === 'string' ? item : item?.platform;
      if (platform) attempted.add(platform);
    });
    // Fallback: if nothing attempted (defensive), use originalPlatforms so the row isn't empty —
    // minus the marketplaces the browser owns, which already have their own chip. Without that
    // subtraction a Depop-only listing showed "Depop" twice, once as work this job did.
    if (attempted.size === 0) {
      const browserOwned = new Set(detail.browserPlatforms || []);
      return (detail.originalPlatforms || []).filter(p => !browserOwned.has(p));
    }
    return [...attempted];
  };

  // Toggle expanded state for a row
  const toggleRow = (listingId) => {
    setExpandedRows(prev => ({
      ...prev,
      [listingId]: !prev[listingId]
    }));
  };

  // Check if a listing has errors
  const hasErrors = (detail) =>
    (detail.errors && detail.errors.length > 0) ||
    (detail.failedDelist && detail.failedDelist.length > 0) ||
    (detail.failedRelist && detail.failedRelist.length > 0);

  // Get error count for a listing
  const getErrorCount = (detail) => {
    if (detail.errors) return detail.errors.length;
    return (detail.failedDelist?.length || 0) + (detail.failedRelist?.length || 0);
  };

  // Get status color and icon
  const getStatusIcon = (platforms, failedPlatforms) => {
    if (!platforms || platforms.length === 0) {
      if (failedPlatforms && failedPlatforms.length > 0) {
        return <Iconify icon="solar:close-circle-bold" width={20} color="error.main" />;
      }
      return <Iconify icon="solar:minus-circle-bold" width={20} color="text.disabled" />;
    }
    return <Iconify icon="solar:check-circle-bold" width={20} color="success.main" />;
  };

  // Get overall status for a listing
  const getOverallStatus = (detail) => rowStatus(detail);

  const summary = delistRelistSummary(results);

  // Get icon based on severity
  const getIconBySeverity = (severity) => {
    if (severity === 'success') return 'solar:check-circle-bold';
    if (severity === 'error') return 'solar:close-circle-bold';
    return 'solar:danger-triangle-bold';
  };

  // Get color based on severity
  const getColorBySeverity = (severity) => {
    if (severity === 'success') return 'success.main';
    if (severity === 'error') return 'error.main';
    return 'warning.main';
  };

  // Extract platform name from failed item (handles both old string format and new object format)
  const getFailedPlatformName = (item) => {
    if (typeof item === 'string') return item;
    return item?.platform || 'Unknown';
  };

  // Extract error message from failed item
  const getFailedErrorMessage = (item) => {
    if (typeof item === 'string') return null;
    return item?.error || null;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Iconify
            icon={getIconBySeverity(summary.severity)}
            width={24}
            sx={{
              color: getColorBySeverity(summary.severity)
            }}
          />
          <span>Delist & Relist Results</span>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          <Alert severity={summary.severity}>{summary.message}</Alert>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={40} />
                  <TableCell><strong>Listing</strong></TableCell>
                  <TableCell align="center"><strong>Marketplaces</strong></TableCell>
                  <TableCell align="center"><strong>Delist</strong></TableCell>
                  <TableCell align="center"><strong>Relist</strong></TableCell>
                  <TableCell align="center"><strong>Status</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {details.map((detail, index) => {
                  const status = getOverallStatus(detail);
                  const listingHasErrors = hasErrors(detail);
                  const errorCount = getErrorCount(detail);
                  const isExpanded = expandedRows[detail.listingId];

                  return (
                    <>
                      <TableRow key={detail.listingId || index} hover>
                        <TableCell>
                          {listingHasErrors && (
                            <IconButton
                              size="small"
                              onClick={() => toggleRow(detail.listingId)}
                            >
                              <Iconify
                                icon={isExpanded ? 'solar:alt-arrow-up-outline' : 'solar:alt-arrow-down-outline'}
                                width={16}
                              />
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                              {detail.title}
                            </Typography>
                            {listingHasErrors && (
                              <Chip
                                label={`${errorCount} error${errorCount !== 1 ? 's' : ''}`}
                                size="small"
                                color="error"
                                variant="outlined"
                                sx={{ width: 'fit-content', fontSize: '0.65rem', height: 18 }}
                              />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell align="center">
                          {(() => {
                            const platforms = platformsForRow(detail);
                            const browserPlatforms = detail.browserPlatforms || [];
                            if (platforms.length === 0 && browserPlatforms.length === 0) {
                              return (
                                <Typography variant="caption" color="text.disabled">—</Typography>
                              );
                            }
                            return (
                              <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap" useFlexGap>
                                {platforms.map((platform) => (
                                  <Chip
                                    key={platform}
                                    label={getPlatformDisplayName(platform)}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem', height: 20 }}
                                  />
                                ))}
                                {browserPlatforms.map((platform) => (
                                  <Tooltip key={`browser-${platform}`} title="Handled in this browser">
                                    <Chip
                                      label={getPlatformDisplayName(platform)}
                                      size="small"
                                      color="info"
                                      variant="outlined"
                                      sx={{ fontSize: '0.7rem', height: 20 }}
                                    />
                                  </Tooltip>
                                ))}
                              </Stack>
                            );
                          })()}
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                            {getStatusIcon(detail.delistedFrom, detail.failedDelist)}
                            <Typography variant="caption" color="text.secondary">
                              {detail.delistedFrom?.length || 0}/{(detail.delistedFrom?.length || 0) + (detail.failedDelist?.length || 0)}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                            {getStatusIcon(detail.relistedTo, detail.failedRelist)}
                            <Typography variant="caption" color="text.secondary">
                              {detail.relistedTo?.length || 0}/{detail.delistedFrom?.length || 0}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={status.label}
                            color={status.color}
                            size="small"
                            sx={{ minWidth: 70 }}
                          />
                        </TableCell>
                      </TableRow>

                      {/* Expanded error details row */}
                      {listingHasErrors && (
                        <TableRow>
                          <TableCell colSpan={6} sx={{ py: 0, borderBottom: isExpanded ? 1 : 0, borderColor: 'divider' }}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ py: 2, px: 2, bgcolor: 'error.lighter', borderRadius: 1, my: 1 }}>
                                <Typography variant="subtitle2" color="error.dark" gutterBottom>
                                  What went wrong:
                                </Typography>

                                {/* Use new errors array if available */}
                                {detail.errors && detail.errors.length > 0 ? (
                                  <Stack spacing={1}>
                                    {detail.errors.map((err, errIndex) => (
                                      <Box key={errIndex} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                        <Chip
                                          label={`${err.phase === 'delist' ? 'Delist' : 'Relist'} - ${getPlatformDisplayName(err.platform)}`}
                                          size="small"
                                          color="error"
                                          variant="outlined"
                                          sx={{ fontSize: '0.65rem', height: 20, flexShrink: 0 }}
                                        />
                                        <Typography variant="caption" color="error.dark" sx={{ wordBreak: 'break-word' }}>
                                          {err.message}
                                        </Typography>
                                      </Box>
                                    ))}
                                  </Stack>
                                ) : (
                                  /* Fallback for old format */
                                  <Stack spacing={1}>
                                    {detail.failedDelist?.map((item, errIndex) => (
                                      <Box key={`delist-${errIndex}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                        <Chip
                                          label={`Delist - ${getPlatformDisplayName(getFailedPlatformName(item))}`}
                                          size="small"
                                          color="error"
                                          variant="outlined"
                                          sx={{ fontSize: '0.65rem', height: 20, flexShrink: 0 }}
                                        />
                                        <Typography variant="caption" color="error.dark" sx={{ wordBreak: 'break-word' }}>
                                          {getFailedErrorMessage(item) || `Couldn't take it off ${getPlatformDisplayName(getFailedPlatformName(item))}, so it's still for sale there.`}
                                        </Typography>
                                      </Box>
                                    ))}
                                    {detail.failedRelist?.map((item, errIndex) => (
                                      <Box key={`relist-${errIndex}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                        <Chip
                                          label={`Relist - ${getPlatformDisplayName(getFailedPlatformName(item))}`}
                                          size="small"
                                          color="error"
                                          variant="outlined"
                                          sx={{ fontSize: '0.65rem', height: 20, flexShrink: 0 }}
                                        />
                                        <Typography variant="caption" color="error.dark" sx={{ wordBreak: 'break-word' }}>
                                          {getFailedErrorMessage(item) || `Couldn't post it again on ${getPlatformDisplayName(getFailedPlatformName(item))}.`}
                                        </Typography>
                                      </Box>
                                    ))}
                                  </Stack>
                                )}

                                {/* Show general message if present */}
                                {detail.message && (
                                  <Typography variant="caption" color="error.dark" sx={{ mt: 1, display: 'block' }}>
                                    {detail.message}
                                  </Typography>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

DelistRelistResultsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  results: PropTypes.shape({
    total: PropTypes.number,
    success: PropTypes.number,
    partial: PropTypes.number,
    failed: PropTypes.number,
    skipped: PropTypes.number,
    hasSelection: PropTypes.bool,
    details: PropTypes.arrayOf(
      PropTypes.shape({
        listingId: PropTypes.string,
        title: PropTypes.string,
        originalPlatforms: PropTypes.array,
        delistedFrom: PropTypes.array,
        relistedTo: PropTypes.array,
        failedDelist: PropTypes.array,
        failedRelist: PropTypes.array,
        errors: PropTypes.arrayOf(
          PropTypes.shape({
            phase: PropTypes.string,
            platform: PropTypes.string,
            message: PropTypes.string
          })
        ),
        status: PropTypes.string,
        message: PropTypes.string
      })
    )
  })
};
